<template>
  <div
    ref="rootEl"
    class="bottom-sheet"
    :class="{ 'bottom-sheet--desktop': isDesktopLayout, 'bottom-sheet--dragging': isDragging }"
    :style="rootStyle"
  >
    <div
      v-if="!isDesktopLayout"
      ref="handleEl"
      class="bottom-sheet-handle"
      role="button"
      tabindex="0"
      :aria-label="isPeek ? 'Expand panel' : 'Collapse panel'"
      @pointerdown="onPointerDown"
      @keydown.enter="toggleHandleTap"
      @keydown.space.prevent="toggleHandleTap"
    >
      <span class="bottom-sheet-grip"></span>
      <slot name="header"></slot>
    </div>

    <div v-else class="bottom-sheet-desktop-header">
      <slot name="header"></slot>
    </div>

    <div class="bottom-sheet-body" :class="{ 'bottom-sheet-body--peek': isPeek }">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Generic draggable bottom-sheet primitive.
 *
 * No panel/sheet component in this codebase today supports drag-to-resize,
 * peek/default/expanded snap states, or safe-area-inset handling — three
 * existing components (HikeMeasurementPanel.vue, TrailDataCard.vue,
 * HikeTrailsPanel.vue) each hand-roll their own fixed-position CSS behind a
 * plain open/closed boolean. This is the shared primitive going forward;
 * elevation-panel-v2 is its first consumer, not its only intended one.
 *
 * Drag uses Pointer Events (not Touch Events) so mouse and touch share one
 * code path and `setPointerCapture` guarantees the drag keeps receiving move
 * events even if the pointer slides off the handle. Only the header/handle
 * strip has a drag listener — the content body never does — so this never
 * competes with gestures (e.g. chart scrub) happening inside the body.
 *
 * On desktop (`matchMedia('(max-width: 768px)')`, matching the convention
 * already used in TrailDataCard.vue) this renders as a static floating panel
 * sized to whatever snap key the caller's `modelValue` is currently set to,
 * with no drag handle and no in-component way to change snaps — the caller's
 * own header content (rendered via the `header` slot) is expected to drive
 * snap changes itself, if the consumer wants that.
 */
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

export interface SnapPoint {
  key: string;
  /** A CSS height value: px number, 'auto', or a string like '40vh'. */
  heightPx: number | 'auto' | string;
}

const props = withDefaults(
  defineProps<{
    snapPoints: SnapPoint[];
    modelValue: string;
    initialSnap?: string;
    forceDesktopLayout?: boolean;
  }>(),
  {
    initialSnap: undefined,
    forceDesktopLayout: false,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', key: string): void;
  (e: 'dragStart'): void;
  (e: 'dragEnd', key: string): void;
}>();

const rootEl = ref<HTMLElement | null>(null);
const handleEl = ref<HTMLElement | null>(null);
const isDesktopMedia = ref(false);
const isDragging = ref(false);
const dragTranslateY = ref(0);

const isDesktopLayout = computed(() => props.forceDesktopLayout || isDesktopMedia.value);
const peekSnapKey = computed(() => props.snapPoints[0]?.key);

const activeSnap = computed(() => props.modelValue || props.initialSnap || peekSnapKey.value);
const isPeek = computed(() => activeSnap.value === peekSnapKey.value);

function heightFor(key: string | undefined): string {
  const point = props.snapPoints.find((p) => p.key === key) ?? props.snapPoints[0];
  if (!point) return 'auto';
  return typeof point.heightPx === 'number' ? `${point.heightPx}px` : point.heightPx;
}

const rootStyle = computed(() => {
  if (isDesktopLayout.value) {
    return { height: heightFor(activeSnap.value) };
  }
  if (isDragging.value) {
    // Compositor-only transform during drag — cheap, no layout thrash. The
    // real height is only committed on release (see settleTo).
    return {
      height: heightFor(activeSnap.value),
      transform: `translateY(${dragTranslateY.value}px)`,
      transition: 'none',
    };
  }
  return { height: heightFor(activeSnap.value), transform: 'translateY(0)' };
});

function updateDesktopMedia(): void {
  isDesktopMedia.value = mediaQueryList?.matches ?? false;
}

let mediaQueryList: MediaQueryList | null = null;
onMounted(() => {
  // matchMedia is unavailable in some test environments (e.g. jsdom by
  // default) — fall back to mobile layout rather than crashing; real
  // browsers all support it.
  mediaQueryList = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 769px)') : null;
  updateDesktopMedia();
  mediaQueryList?.addEventListener('change', updateDesktopMedia);

  const root = document.documentElement;
  if (!root.style.getPropertyValue('--safe-area-bottom')) {
    root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom, 0px)');
  }
});

onBeforeUnmount(() => {
  mediaQueryList?.removeEventListener('change', updateDesktopMedia);
});

// --- Drag state machine -----------------------------------------------

let pointerId: number | null = null;
let startClientY = 0;
let startHeightPx = 0;
let lastMoveY = 0;
let lastMoveTime = 0;
let velocity = 0; // px/ms, positive = moving down

function pxHeightOf(key: string): number {
  const el = rootEl.value;
  if (!el) return 0;
  // Resolve 'vh'/'auto' snap points against the real rendered height by
  // temporarily reading the target's computed height would require a DOM
  // measurement pass; instead we measure the *current* element height as the
  // drag's starting point (accurate regardless of unit) and only need target
  // heights, in px, for the ones we snap TO — resolved via a hidden probe.
  return resolveHeightPx(key, el);
}

function resolveHeightPx(key: string, referenceEl: HTMLElement): number {
  const point = props.snapPoints.find((p) => p.key === key);
  if (!point) return referenceEl.getBoundingClientRect().height;
  if (typeof point.heightPx === 'number') return point.heightPx;
  if (point.heightPx === 'auto') return referenceEl.scrollHeight;
  // 'Nvh' or similar CSS length — resolve against the viewport/parent via a
  // throwaway probe element so we can compare snap points numerically during
  // drag (drag itself still animates via the real CSS value on release).
  const probe = document.createElement('div');
  probe.style.position = 'fixed';
  probe.style.visibility = 'hidden';
  probe.style.height = String(point.heightPx);
  document.body.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);
  return px;
}

function onPointerDown(event: PointerEvent): void {
  if (isDesktopLayout.value) return;
  // Header slot content (the collapse chevron, "Select range", fullscreen,
  // etc. in elevation-panel-v2) renders INSIDE this handle strip so the whole
  // header is one drag/tap surface. Without this guard, tapping any of those
  // buttons ALSO starts a drag/tap-toggle sequence here — setPointerCapture
  // below hijacks the pointer, and the resulting synthetic `click` never
  // reaches the button, so its own handler silently never fires. Letting a
  // real interactive control keep the event lets its own click/keyboard
  // handling work while a tap/drag anywhere else on the handle (grip, title,
  // blank space) still resizes/toggles the sheet as before.
  //
  // NOTE: deliberately NOT matching `[role="button"]` here — the handle strip
  // itself carries `role="button"` for its own a11y label (see the template),
  // so that selector would match the handle on every tap, not just genuine
  // nested controls, and disable this entirely.
  const target = event.target as HTMLElement | null;
  if (target?.closest('button, a, input, select, textarea')) return;

  const handle = handleEl.value;
  const root = rootEl.value;
  if (!handle || !root) return;

  pointerId = event.pointerId;
  // jsdom (our test environment) doesn't implement Pointer Capture; guard so
  // tests can still exercise the drag state machine.
  handle.setPointerCapture?.(pointerId);
  startClientY = event.clientY;
  startHeightPx = root.getBoundingClientRect().height;
  lastMoveY = event.clientY;
  lastMoveTime = performance.now();
  velocity = 0;
  isDragging.value = true;
  dragTranslateY.value = 0;
  emit('dragStart');

  handle.addEventListener('pointermove', onPointerMove);
  handle.addEventListener('pointerup', onPointerUp);
  handle.addEventListener('pointercancel', onPointerUp);
}

function onPointerMove(event: PointerEvent): void {
  if (pointerId == null || event.pointerId !== pointerId) return;
  const now = performance.now();
  const dt = now - lastMoveTime;
  if (dt > 0) velocity = (event.clientY - lastMoveY) / dt;
  lastMoveY = event.clientY;
  lastMoveTime = now;

  const deltaY = event.clientY - startClientY;
  // Dragging down (positive deltaY) should shrink the sheet, i.e. move its
  // top edge down => translateY positive; clamp so it can't be dragged
  // taller than the largest snap point or fully off-screen.
  const maxHeight = Math.max(...props.snapPoints.map((p) => resolveHeightPx(p.key, rootEl.value!)));
  const proposedHeight = startHeightPx - deltaY;
  const clampedHeight = Math.max(0, Math.min(maxHeight, proposedHeight));
  dragTranslateY.value = startHeightPx - clampedHeight;
}

function onPointerUp(event: PointerEvent): void {
  if (pointerId == null || event.pointerId !== pointerId) return;
  const handle = handleEl.value;
  handle?.removeEventListener('pointermove', onPointerMove);
  handle?.removeEventListener('pointerup', onPointerUp);
  handle?.removeEventListener('pointercancel', onPointerUp);

  const movedPx = Math.abs(event.clientY - startClientY);
  const elapsedMs = performance.now() - lastMoveTime + 1;
  const isTap = movedPx < 8 && elapsedMs < 300;

  isDragging.value = false;
  pointerId = null;

  if (isTap) {
    toggleHandleTap();
    return;
  }

  const finalHeight = startHeightPx - dragTranslateY.value;
  settleTo(finalHeight);
}

function toggleHandleTap(): void {
  const idx = props.snapPoints.findIndex((p) => p.key === activeSnap.value);
  const nextIdx = idx <= 0 ? Math.min(1, props.snapPoints.length - 1) : 0;
  emit('update:modelValue', props.snapPoints[nextIdx].key);
  emit('dragEnd', props.snapPoints[nextIdx].key);
}

const FLICK_VELOCITY_THRESHOLD = 0.5; // px/ms

function settleTo(finalHeightPx: number): void {
  const root = rootEl.value;
  if (!root) return;
  const heights = props.snapPoints.map((p) => ({ key: p.key, px: resolveHeightPx(p.key, root) }));

  let targetKey: string;
  if (Math.abs(velocity) > FLICK_VELOCITY_THRESHOLD) {
    // Flick: snap in the direction of motion (velocity > 0 = moving down =
    // shrinking = toward a smaller snap point), even if not the nearest.
    const sorted = [...heights].sort((a, b) => a.px - b.px);
    const currentIdx = sorted.findIndex((h) => h.key === activeSnap.value);
    const dirIdx = velocity > 0 ? Math.max(0, currentIdx - 1) : Math.min(sorted.length - 1, currentIdx + 1);
    targetKey = sorted[dirIdx]?.key ?? activeSnap.value;
  } else {
    targetKey = heights.reduce((closest, h) =>
      Math.abs(h.px - finalHeightPx) < Math.abs(closest.px - finalHeightPx) ? h : closest
    ).key;
  }

  dragTranslateY.value = 0;
  emit('update:modelValue', targetKey);
  emit('dragEnd', targetKey);
}

watch(
  () => props.modelValue,
  () => {
    dragTranslateY.value = 0;
  }
);
</script>

<style scoped>
.bottom-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  background: rgba(23, 24, 29, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(172, 176, 191, 0.3);
  color: #fff;
  overflow: hidden;
  transition: height 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  padding-bottom: var(--safe-area-bottom, 0px);
}

.bottom-sheet--dragging {
  transition: none;
}

/* Mobile: bottom sheet, rounded top corners only. */
.bottom-sheet:not(.bottom-sheet--desktop) {
  border-radius: 16px 16px 0 0;
  border-bottom: none;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.35);
}

/* Desktop: floating panel, rounded on all corners. */
.bottom-sheet--desktop {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: height 0.2s ease;
}

.bottom-sheet-handle {
  flex-shrink: 0;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.bottom-sheet-handle:active {
  cursor: grabbing;
}

.bottom-sheet-grip {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.25);
}

.bottom-sheet-desktop-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
}

.bottom-sheet-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Allow vertical scroll of content; never drag-resize from here. */
  touch-action: pan-y;
}

.bottom-sheet-body--peek {
  overflow: hidden;
}
</style>
